// @ts-ignore - import map resolve
import ZerezesApiService from '@theme/zerezesapi-service';

const BRAZIL_DIALING_CODE = '55';
const PHONE_INVALID_MESSAGE =
  'Informe o telefone com DDI e DDD, ex.: +55 (21) 99999-9999';

/**
 * Componente de formulário de captação de leads, com campos configuráveis via blocks.
 *
 * Destino dos dados:
 * - HubSpot: captura automática ("formulários não HubSpot") pelo script de
 *   rastreamento (snippets/hubspot-embed.liquid). Os campos usam os nomes
 *   internos do HubSpot (firstname, email, phone) pra mapear direto nas
 *   propriedades do contato. Não há chamada aqui: o script lê o <form> no envio.
 * - Clube Zerezes: registro via API da Zerezes (registerInCrm).
 *
 * O envio é por JS (sem recarregar a página). Não use stopPropagation no
 * submit: o script do HubSpot precisa receber o evento.
 */
class LeadCaptureForm extends HTMLElement {
  constructor() {
    super();

    this.formMessageSuccess =
      this.dataset.formMessageSuccess ||
      'Obrigado! Você foi inscrito com sucesso.';
    this.trackingSource = this.dataset.trackingSource || 'lead-capture-form';

    this.form = this.querySelector('[data-target="form"]');
    this.button = this.querySelector('[data-target="button"]');
    this.feedback = this.querySelector('[data-target="feedback"]');

    this.setupEvents();
  }

  _logInfo(message, data = null) {
    if (this._isDevelopment()) {
      // eslint-disable-next-line no-console
      console.log(`[LeadCaptureForm] ${message}`, data || '');
    }
  }

  _logError(message, error = null) {
    // eslint-disable-next-line no-console
    console.error(`[LeadCaptureForm] ${message}`, error || '');
  }

  _logWarn(message) {
    if (this._isDevelopment()) {
      // eslint-disable-next-line no-console
      console.warn(`[LeadCaptureForm] ${message}`);
    }
  }

  _isDevelopment() {
    return window.location.hostname === '127.0.0.1';
  }

  setupEvents() {
    if (this.form && this.form instanceof HTMLFormElement) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    document.addEventListener('click', this.handleAnchorClick.bind(this));

    this.querySelectorAll('input[data-field-type="phone"]').forEach(input => {
      if (input instanceof HTMLInputElement) this.setupPhoneInput(input);
    });
  }

  /**
   * Telefone sempre com DDI e DDD: máscara "+55 (21) 99999-9999" enquanto
   * digita e validação nativa (setCustomValidity), que bloqueia o envio.
   * Fica nos eventos do campo, não no submit — o HubSpot lê o valor já
   * formatado direto do <form>.
   * @param {HTMLInputElement} input
   */
  setupPhoneInput(input) {
    // Campo vazio ganha o DDI do Brasil ao focar; sai de novo se ninguém digitar
    input.addEventListener('focus', () => {
      if (!input.value.trim()) input.value = `+${BRAZIL_DIALING_CODE} `;
    });
    input.addEventListener('blur', () => {
      if (input.value.replace(/\D/g, '') === BRAZIL_DIALING_CODE) input.value = '';
      this.validatePhone(input);
    });
    input.addEventListener('input', () => {
      input.value = this.formatPhone(input.value);
      this.validatePhone(input);
    });

    this.validatePhone(input);
  }

  /**
   * Sem "+", assume Brasil: "21999998888" → "+55 (21) 99999-8888".
   * Brasil é formatado; outros DDIs ficam como "+<dígitos>".
   * @param {string} rawValue
   * @returns {string}
   */
  formatPhone(rawValue) {
    const hasPlus = rawValue.trim().startsWith('+');
    let digits = rawValue.replace(/\D/g, '');
    if (!digits) return hasPlus ? '+' : '';

    const looksInternational =
      hasPlus || (digits.startsWith(BRAZIL_DIALING_CODE) && digits.length > 11);
    if (!looksInternational) digits = BRAZIL_DIALING_CODE + digits;

    if (!digits.startsWith(BRAZIL_DIALING_CODE)) {
      return `+${digits.slice(0, 15)}`;
    }

    let national = digits.slice(BRAZIL_DIALING_CODE.length);
    // DDI digitado de novo por cima do "+55" (ex.: "+55 55 21 99999-8888")
    if (national.length > 11 && national.startsWith(BRAZIL_DIALING_CODE)) {
      national = national.slice(BRAZIL_DIALING_CODE.length);
    }
    national = national.slice(0, 11);
    const areaCode = national.slice(0, 2);
    const number = national.slice(2);
    const splitAt = number.length > 8 ? 5 : 4;

    let formatted = `+${BRAZIL_DIALING_CODE}`;
    if (areaCode) formatted += ` (${areaCode}`;
    if (areaCode.length === 2 && number) formatted += `) ${number.slice(0, splitAt)}`;
    if (number.length > splitAt) formatted += `-${number.slice(splitAt)}`;
    return formatted;
  }

  /**
   * Brasil: DDD válido (11–99) + 8 dígitos (fixo) ou 9 começando com 9 (celular).
   * Outros DDIs: entre 10 e 15 dígitos no total (DDI + DDD + número).
   * @param {HTMLInputElement} input
   */
  validatePhone(input) {
    const digits = input.value.replace(/\D/g, '');

    if (!digits || digits === BRAZIL_DIALING_CODE) {
      // Vazio: o "required" do campo cuida (ou é opcional)
      input.setCustomValidity('');
      return;
    }

    let isValid;
    if (digits.startsWith(BRAZIL_DIALING_CODE)) {
      const national = digits.slice(BRAZIL_DIALING_CODE.length);
      const areaCode = Number(national.slice(0, 2));
      const number = national.slice(2);
      isValid =
        areaCode >= 11 &&
        areaCode <= 99 &&
        (number.length === 8 || (number.length === 9 && number.startsWith('9')));
    } else {
      isValid = digits.length >= 10 && digits.length <= 15;
    }

    input.setCustomValidity(isValid ? '' : PHONE_INVALID_MESSAGE);
  }

  /**
   * Links da página que apontam pra esta section (ex.: botão "inscreva-se"
   * com href="#lead-capture") rolam até o formulário e já focam o primeiro campo.
   * @param {MouseEvent} event
   */
  handleAnchorClick(event) {
    const target = /** @type {Element | null} */ (event.target);
    const link = target?.closest?.('a[href^="#"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const section = this.closest('.lead-capture-form-section');
    const targetId = decodeURIComponent(link.hash.slice(1));
    if (!section || !targetId || section.id !== targetId) return;

    const firstInput = this.querySelector('input:not([type="hidden"]):not([disabled])');
    if (!(firstInput instanceof HTMLInputElement)) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    section.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    // preventScroll: o scrollIntoView acima já cuida da rolagem (com animação)
    firstInput.focus({ preventScroll: true });
    history.replaceState(null, '', `#${targetId}`);
  }

  reset() {
    if (this.feedback) {
      this.feedback.innerHTML = '';
    }

    if (this.form && this.form instanceof HTMLFormElement) {
      this.form.reset();
      this.form.classList.remove('hidden');
      this.form.classList.remove('--submitted');
    }
  }

  showSuccess() {
    if (this.form) {
      this.form.classList.add('hidden');
    }

    if (this.feedback) {
      this.feedback.innerHTML = `<p class="lead-capture-form__success">${this.formMessageSuccess}</p>`;
    }

    setTimeout(() => this.reset(), 10000);
  }

  /**
   * @param {Event} event
   */
  handleSubmit(event) {
    event.preventDefault();

    if (this.form) {
      this.form.classList.add('--submitted');
    }

    if (this.isSubmitting || !this.validateForm()) {
      return;
    }

    const values = this.collectFieldValues();
    const email = values.email || '';
    const name = values.name || '';

    if (!email) {
      this._logWarn(
        'Nenhum campo de e-mail configurado ou preenchido neste formulário'
      );
      return;
    }

    this.setSubmitting(true);
    this.trackAnalyticsEvent();

    // O HubSpot já capturou o envio a partir do <form>; o sucesso não depende
    // do clube (erro lá só é logado) pra não induzir reenvio/lead duplicado.
    this.registerInCrm(email, name).finally(() => {
      this.setSubmitting(false);
      this.showSuccess();
    });
  }

  /**
   * Trava o botão enquanto o envio está em andamento (evita lead duplicado
   * por clique duplo) e mostra o estado de "enviando".
   * @param {boolean} submitting
   */
  setSubmitting(submitting) {
    this.isSubmitting = submitting;
    this.form?.setAttribute('aria-busy', String(submitting));

    if (!(this.button instanceof HTMLButtonElement)) return;
    if (submitting) {
      this.buttonLabel = this.button.textContent;
      this.button.textContent = 'enviando…';
    } else if (this.buttonLabel !== undefined) {
      this.button.textContent = this.buttonLabel;
    }
    this.button.disabled = submitting;
  }

  validateForm() {
    if (!this.form || !(this.form instanceof HTMLFormElement)) {
      return false;
    }

    if (this.form.checkValidity) {
      return this.form.checkValidity();
    }

    return true;
  }

  /**
   * Lê todos os inputs marcados com data-field-type e retorna seus valores agrupados por tipo
   * @returns {{[key: string]: string}}
   */
  collectFieldValues() {
    const values = {};
    const inputs = this.querySelectorAll('[data-field-type]');

    inputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        const fieldType = input.dataset.fieldType;
        if (fieldType) {
          values[fieldType] = input.value.trim();
        }
      }
    });

    return values;
  }

  /**
   * @param {string} email
   * @param {string} name
   */
  async registerInCrm(email, name) {
    try {
      const zapiService = new ZerezesApiService();

      if (!zapiService.isConfigured()) {
        this._logWarn('Serviço da API Zerezes não está configurado');
        return;
      }

      const bearerToken = this.getAuthToken();

      const result = await zapiService.registerCustomerFromCrmForm(
        email,
        name,
        bearerToken
      );

      this.handleCrmResponse(result, zapiService);
    } catch (error) {
      this._logError('Erro no registro CRM', error);
    }
  }

  getAuthToken() {
    const tokenInput = document.querySelector('.zapi-public-refresh-token');
    if (
      tokenInput &&
      tokenInput instanceof HTMLInputElement &&
      tokenInput.value
    ) {
      return tokenInput.value;
    }

    // @ts-ignore - propriedade configurada pelo tema Shopify
    if (
      typeof window !== 'undefined' &&
      // @ts-ignore
      window.z &&
      // @ts-ignore
      window.z.zapi_public_token
    ) {
      // @ts-ignore
      return window.z.zapi_public_token;
    }

    return null;
  }

  /**
   * @param {any} result
   * @param {ZerezesApiService} zapiService
   */
  handleCrmResponse(result, zapiService) {
    if (result?.status === 401 && result?.error === 'refresh') {
      this._logInfo('Token precisa ser renovado, pulando registro CRM');
      return;
    }

    if (result?.refreshToken) {
      zapiService.updateRefreshTokens(result.refreshToken);
    }

    if (result?.success) {
      this._logInfo('Cliente registrado no CRM com sucesso');
    }
  }

  trackAnalyticsEvent() {
    try {
      // @ts-ignore - dataLayer é específico do GTM
      if (typeof window !== 'undefined' && window.dataLayer) {
        // @ts-ignore
        window.dataLayer.push({
          event: 'lead_capture_form',
          data: { action: 'signup', label: this.trackingSource },
        });
      }
    } catch (error) {
      this._logWarn('Erro no tracking de analytics');
    }
  }

  /**
   * @param {string} fullName
   * @returns {string}
   */
  extractFirstName(fullName) {
    if (!fullName) return '';
    const names = this.formatNameParts(fullName.split(' '));
    return names[0] || '';
  }

  /**
   * @param {string} fullName
   * @returns {string}
   */
  extractLastName(fullName) {
    if (!fullName) return '';
    const names = fullName.split(' ');
    const lastNames = this.formatNameParts(names.slice(1));
    return lastNames.join(' ') || '';
  }

  /**
   * @param {string[]} nameParts
   * @returns {string[]}
   */
  formatNameParts(nameParts) {
    return nameParts
      .filter(part => part && part.trim())
      .map(part => {
        const trimmed = part.trim();
        if (!trimmed) return '';

        const lowerName = trimmed.toLowerCase();

        if (['da', 'de', 'do', 'das', 'dos'].includes(lowerName)) {
          return lowerName;
        }

        if (lowerName.length === 1) {
          return lowerName.toUpperCase();
        }

        return this.capitalizeWord(lowerName);
      });
  }

  /**
   * @param {string} word
   * @returns {string}
   */
  capitalizeWord(word) {
    if (typeof word !== 'string' || !word) return '';
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
}

customElements.define('lead-capture-form', LeadCaptureForm);
